import { OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { Subject } from 'rxjs';
import { ClinicSetup, TemperatureUnit } from 'src/API';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { CustomValidator } from 'src/app/intake-form/intake-form-validators';
import { CreateClinicSetupInput, UpdateClinicSetupInput } from './../../../API';
import { AbstractControlsMap } from './../../shared/dynamic-form-group/dynamic-form-group';
import { notificationMap } from './customizable-welcome-notification/notification-map';
import { EmailNotificationType } from './email-notification-recipients-setup/email-notification-recipients.model';
import { Layout } from './layouts/layouts.service';
import { defaultTextFavoriteMap } from './text-favorite/text-favorite-model';
import { defaultTreatmentTemplateMap } from './treatment-template/treatment-template-model';

export const DECLINED_CONSENT_WARNINGS_EXTRA_CONFIG_KEY = 'showDeclinedConsentWarnings';

// On unless the clinic explicitly turned it off, so clinics that never saved the setting keep it.
export function isDeclinedConsentWarningEnabled(clinicSetup: ClinicSetup | null): boolean {
  const extraConfig = safeJsonParse(clinicSetup && clinicSetup.extraConfig);
  return !(
    extraConfig &&
    typeof extraConfig === 'object' &&
    extraConfig[DECLINED_CONSENT_WARNINGS_EXTRA_CONFIG_KEY] === false
  );
}

interface ClinicSetupFormControls extends AbstractControlsMap {
  id: FormControl;
  signsLayout: FormControl;
  textFavorites: FormControl;
  treatmentTemplates: FormControl;
  medicalHistoryLayout: FormControl;
  layouts: FormControl;
  defaultQuestionnaireLanguage: FormControl;
  defaultPdfLanguage: FormControl;
  temperatureUnit: FormControl;
  questionnaireExpiry: FormControl;
  welcomeMessageConfig: FormGroup;
  emailNotificationsConfig: EmailNotificationsConfigFormGroup;
  hiddenTextFavorites: FormControl;
  hiddenTreatmentTemplates: FormControl;
  textFavoriteConfig: FormControl;
  questionnaireScoreConfig: FormControl;
  questionnaireNotificationsConfig: FormGroup;
  reminderConfig: ReminderFormGroup;
  generateAiMedicalHistory: FormControl;
  showDeclinedConsentWarnings: FormControl;
}

export class EmailNotificationsConfigFormGroup extends FormGroup {
  constructor() {
    super(
      Object.keys(EmailNotificationType).reduce((acc, type) => {
        acc = {
          ...acc,
          [type]: new FormGroup({
            notificationRecipients: new FormControl(),
            enabled: new FormControl(true)
          })
        };
        return acc;
      }, {})
      // notificationRecipients: new FormControl(),
      // enabled: new FormControl(true)
    );
  }
}

export class WelcomeMessageConfigFormGroup extends FormGroup {
  constructor() {
    super({
      enabled: new FormControl(false),
      message: new FormControl('')
    });
  }
}

export class QuestionnaireNotificationsConfigFormGroup extends FormGroup {
  constructor() {
    super({
      en: new FormControl(),
      es: new FormControl(),
      fr: new FormControl()
    });
  }
}

export class ReminderFormGroup extends FormGroup {
  // Whether the logged-in staff member may edit the reminder settings. Tracked
  // explicitly rather than inferred from the parent's disabled state: enabling
  // any descendant flips every disabled ancestor back to VALID, so a group that
  // is only disabled by cascade cannot be trusted to stay that way.
  private canEdit = true;

  constructor() {
    super({
      numberOfReminder: new FormControl(null, CustomValidator.minRange(1)),
      enabled: new FormControl(false)
    });

    this.valueChanges.subscribe(() => {
      const numberOfReminderValue = this.controls.numberOfReminder.value;

      Object.entries(this.controls).forEach(([key, value]) => {
        if (!['numberOfReminder', 'enabled'].includes(key)) {
          const isReminderSlotActive =
            +key <= numberOfReminderValue && this.controls.enabled.value;

          // onlySelf: toggling a slot must not recompute the ancestors' status.
          // This subscription fires part way through ClinicSetupFormGroup.disable(),
          // where the siblings after reminderConfig are still enabled, so
          // propagating upwards would leave the whole form's status on VALID even
          // though every one of its controls ends up disabled.
          if (!this.canEdit || !isReminderSlotActive || this.isDisabledByParent()) {
            value.disable({
              emitEvent: false,
              onlySelf: true
            });
          } else {
            value.enable({
              emitEvent: false,
              onlySelf: true
            });
          }
        }
      });
    });
  }
  setEditable(canEdit: boolean) {
    this.canEdit = canEdit;
    if (!canEdit) {
      // Covers the toggle and the radio group too, which the slot loop skips.
      this.disable({ emitEvent: false });
    }
  }

  isDisabledByParent(): boolean {
    let ancestor: AbstractControl = this.parent;
    while (ancestor) {
      if (ancestor.disabled) {
        return true;
      }
      ancestor = ancestor.parent;
    }
    return false;
  }

  addReminderControl(
    reminderOrderKey: string,
    numberOfReminderControl: AbstractControl,
    initialValue = null
  ) {
    if (this.controls[reminderOrderKey]) return;
    this.addControl(
      reminderOrderKey,
      new FormControl(
        {
          value: initialValue,
          disabled: !this.canEdit || this.disabled || this.isDisabledByParent()
        },
        [
          this.required(reminderOrderKey, numberOfReminderControl)
          // this.outOfBoundsForQuestionnaireExpiry()
        ]
      )
    );
  }

  required(reminderOrderKey: string, numberOfReminderControl: AbstractControl): ValidatorFn | null {
    return (control: AbstractControl) => {
      if (numberOfReminderControl.value >= +reminderOrderKey) {
        if (control.value) {
          return null;
        }
        return { msg: 'Required' };
      }
    };
  }

  patchValue(reminderConfig) {
    if (!reminderConfig) return;
    this.controls.numberOfReminder.patchValue(reminderConfig.numberOfReminder);
    this.controls.enabled.patchValue(reminderConfig.enabled);
    Object.keys(reminderConfig).forEach(configKey => {
      this.addReminderControl(configKey, this.controls.numberOfReminder, reminderConfig[configKey]);
    });
  }
}

export class ClinicSetupFormGroup extends FormGroup implements OnInit {
  private readonly DEFAULT_DAYS = 14;
  // Snapshot of the parsed extraConfig blob at hydration time. Round-tripped
  // on save (spread before form-tracked keys) so unknown keys persist instead
  // of getting wiped when the form only knows about a subset of extraConfig.
  private loadedExtraConfig: { [key: string]: unknown } = {};
  private _submitted = false;
  public submitted$ = new Subject<boolean>();
  set submitted(submitted: boolean) {
    this._submitted = submitted;
    this.submitted$.next(submitted);
    this.markAllAsTouched();
  }
  get submitted(): boolean {
    return this._submitted;
  }

  controls: ClinicSetupFormControls;

  constructor() {
    super({
      id: new FormControl(),
      textFavorites: new FormControl(),
      treatmentTemplates: new FormControl(),
      layouts: new FormControl(),
      defaultQuestionnaireLanguage: new FormControl(),
      defaultPdfLanguage: new FormControl(),
      temperatureUnit: new FormControl(TemperatureUnit.celsius),
      questionnaireExpiry: new FormControl(),
      // Use the notificationMap for the welcomeMessageConfig FormGroup
      welcomeMessageConfig: new WelcomeMessageConfigFormGroup(),
      emailNotificationsConfig: new EmailNotificationsConfigFormGroup(),
      hiddenTextFavorites: new FormControl(),
      hiddenTreatmentTemplates: new FormControl(),
      textFavoriteConfig: new FormControl(),
      questionnaireScoreConfig: new FormControl(),
      questionnaireNotificationsConfig: new QuestionnaireNotificationsConfigFormGroup(),
      reminderConfig: new ReminderFormGroup(),
      defaultDiscountAmount: new FormControl(0),
      currency: new FormControl('USD'),
      subscriptionPlan: new FormControl(),
      generateAiMedicalHistory: new FormControl(null),
      showDeclinedConsentWarnings: new FormControl(true)
    });
  }

  ngOnInit() {}

  patchValueFromClinicSetup(clinicSetup: ClinicSetup, doctorEmail: string) {
    if (clinicSetup) {
      // this follows a different logic
      delete clinicSetup.treatmentTemplates;
      delete clinicSetup.textFavorites;

      const emailNotificationsConfig = this.extractAndProcessEmailNotificationsConfig(
        clinicSetup,
        doctorEmail
      );

      const defaultTextFavoriteConfig = JSON.stringify({
        impressions: [],
        recommendations: [],
        goals: []
      });

      const extraConfig: { [key: string]: unknown } = JSON.parse(clinicSetup.extraConfig || '{}');
      this.loadedExtraConfig = extraConfig;

      this.patchValue({
        ...clinicSetup,
        temperatureUnit: clinicSetup.temperatureUnit || TemperatureUnit.celsius,
        textFavoriteConfig: JSON.parse(clinicSetup.textFavoriteConfig || defaultTextFavoriteConfig),
        hiddenTextFavorites:
          clinicSetup.hiddenTextFavorites ||
          Object.keys(defaultTextFavoriteMap).filter(
            textFavoriteKey => !defaultTextFavoriteMap[textFavoriteKey].showByDefault
          ),
        hiddenTreatmentTemplates:
          clinicSetup.hiddenTreatmentTemplates ||
          Object.keys(defaultTreatmentTemplateMap).filter(
            treatmentTemplateKey => !defaultTreatmentTemplateMap[treatmentTemplateKey].showByDefault
          ),
        questionnaireScoreConfig: JSON.parse(clinicSetup.questionnaireScoreConfig || '{}'),
        welcomeMessageConfig: JSON.parse(
          clinicSetup.welcomeMessageConfig || JSON.stringify(notificationMap.welcomeMessage.default)
        ),
        questionnaireNotificationsConfig: JSON.parse(
          clinicSetup.questionnaireNotificationsConfig ||
            JSON.stringify(notificationMap.questionnaireMessage.default)
        ),
        reminderConfig: JSON.parse(clinicSetup.reminderConfig || '{}'),
        emailNotificationsConfig,
        subscriptionPlan: clinicSetup.subscriptionPlan,
        generateAiMedicalHistory: extraConfig['generateAiMedicalHistory'],
        showDeclinedConsentWarnings: isDeclinedConsentWarningEnabled(clinicSetup)
      });
    }
  }

  public getLoadedExtraConfig(): { [key: string]: unknown } {
    return { ...this.loadedExtraConfig };
  }

  public mergeLoadedExtraConfig(partial: { [key: string]: unknown }): void {
    this.loadedExtraConfig = {
      ...this.loadedExtraConfig,
      ...partial
    };
  }

  extractAndProcessEmailNotificationsConfig(clinicSetup: ClinicSetup, doctorEmail: string) {
    let emailNotificationsConfig = JSON.parse(clinicSetup.emailNotificationsConfig || '{}');

    if (!emailNotificationsConfig.notificationRecipients) {
      // Default to doctors email
      Object.keys(emailNotificationsConfig).forEach(key => {
        if (!emailNotificationsConfig[key].notificationRecipients) {
          emailNotificationsConfig[key].notificationRecipients = [doctorEmail];
        }
      });
    } else {
      // Update deprecated config
      emailNotificationsConfig = Object.keys(EmailNotificationType).reduce((acc, type) => {
        acc = {
          ...acc,
          [type]: {
            notificationRecipients: emailNotificationsConfig.notificationRecipients,
            enabled: emailNotificationsConfig.enabled
          }
        };
        return acc;
      }, {});
    }
    return emailNotificationsConfig;
  }

  updateClinicSetupInput(): UpdateClinicSetupInput {
    const {
      id,
      layouts,
      defaultPdfLanguage,
      defaultQuestionnaireLanguage,
      temperatureUnit,
      questionnaireExpiry,
      welcomeMessageConfig,
      emailNotificationsConfig,
      hiddenTextFavorites,
      hiddenTreatmentTemplates,
      textFavoriteConfig,
      subscriptionPlan,
      questionnaireNotificationsConfig,
      reminderConfig,
      generateAiMedicalHistory,
      showDeclinedConsentWarnings
    } = this.value;

    const updateInput = {
      id,
      defaultPdfLanguage,
      defaultQuestionnaireLanguage,
      temperatureUnit,
      questionnaireExpiry: questionnaireExpiry || this.DEFAULT_DAYS,
      welcomeMessageConfig: JSON.stringify(welcomeMessageConfig),
      emailNotificationsConfig: JSON.stringify(emailNotificationsConfig),
      hiddenTextFavorites: [...new Set(hiddenTextFavorites)] as string[],
      hiddenTreatmentTemplates: [...new Set(hiddenTreatmentTemplates)] as string[],
      textFavoriteConfig: JSON.stringify(textFavoriteConfig),
      questionnaireNotificationsConfig: JSON.stringify(
        this.controls.questionnaireNotificationsConfig.value
      ),
      reminderConfig: JSON.stringify(reminderConfig),
      subscriptionPlan
    };
    updateInput['extraConfig'] = JSON.stringify({
      ...this.loadedExtraConfig,
      generateAiMedicalHistory,
      [DECLINED_CONSENT_WARNINGS_EXTRA_CONFIG_KEY]: showDeclinedConsentWarnings !== false
    });
    if (this.controls.layouts.dirty) {
      updateInput['layouts'] = (layouts as Layout[]).map((layout: any) => {
        if (layout['signsLayout']) {
          const { preventShow, ...rest } = layout['signsLayout'];
          return {
            ...layout,
            signsLayout: rest,
            symptomSettings: JSON.stringify(layout.symptomSettings)
          };
        }
        return { ...layout, symptomSettings: JSON.stringify(layout.symptomSettings) };
      });
    }
    return updateInput;
  }

  getCreateClinicSetupInput(clinicSetupClinicId: string): CreateClinicSetupInput {
    const createClinicSetupInput: CreateClinicSetupInput = { clinicSetupClinicId };

    return createClinicSetupInput;
  }
}
